import { SUPPORTED_MIME_TYPES, type PropertyType } from '@/backend/types'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

const xlsxMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export const filterFilesByMimeTypes = (files: File[], mimeTypes: string[] | 'all') => {
  if (mimeTypes === 'all') {
    return {
      supportedFiles: [...files],
      unsupportedFiles: [],
      unsupportedMimeTypes: [],
    }
  }

  const supportedFiles: File[] = []
  const unsupportedFiles: File[] = []
  const unsupportedMimeTypes = new Set<string>()

  files.forEach((file) => {
    if (mimeTypes.includes(file.type)) {
      supportedFiles.push(file)
    } else {
      unsupportedFiles.push(file)
      unsupportedMimeTypes.add(file.type)
    }
  })

  return {
    supportedFiles,
    unsupportedFiles,
    unsupportedMimeTypes: Array.from(unsupportedMimeTypes),
  }
}

/**
 * Returns all MIME types that are supported by the application, for a given property type.
 *
 * This composable can sometimes seem kind of useless, because it might
 * return the same array everywhere, so it could simply be a constant.
 *
 * However, we often add MIME types behind feature flags. When
 * we do so it's useful to only need to update one place (this composable).
 *
 * Xlsx only works when used with a page splitter type at the moment (2025-01-30).
 * Agent fields (not library or chat) can pass field as an argument to indicate
 * that they can accept Xlsx.
 */
export const useSupportedMimeTypes = (type: MaybeRefOrGetter<PropertyType>) => {
  const supportedMimeTypes = computed<string[] | undefined>(() => {
    switch (toValue(type)) {
      case 'file_collection':
        return [...SUPPORTED_MIME_TYPES, xlsxMimeType]
      case 'file':
        return SUPPORTED_MIME_TYPES
      case 'data':
        return undefined
      default:
        throw new Error(`Unsupported property type: ${toValue(type)}`)
    }
  })

  return {
    supportedMimeTypes,
    filterFilesByMimeTypes: (files: File[]) =>
      filterFilesByMimeTypes(files, supportedMimeTypes.value || 'all'),
  }
}
